// All content for the AI coaching pages (FR + EN).
// Centralised here so wording changes don't require touching components.

export const SITE_URL = "https://bluebloodfr.tech";

export const COACHING_PATHS = {
  fr: "/cours-ia",
  en: "/ai-coaching",
};

export const COACHING_PROJECT_TYPE = "AI Coaching / Cours IA";

/* ------------------------------------------------------------------ */
/*  French content                                                     */
/* ------------------------------------------------------------------ */
export const FR = {
  locale: "fr",
  htmlLang: "fr-FR",
  path: COACHING_PATHS.fr,
  altPath: COACHING_PATHS.en,
  altLabel: "English",
  altLang: "en-GB",

  seo: {
    title: "Cours IA, ChatGPT et Python | Louis-Melchior Giraud",
    description:
      "Cours particuliers d’IA, ChatGPT, Python et machine learning avec Louis-Melchior Giraud. En français ou anglais, en ligne et au Luxembourg.",
    ogTitle: "Cours particuliers d’IA et de Python avec Louis-Melchior Giraud",
    ogDescription:
      "Accompagnement personnalisé en intelligence artificielle, ChatGPT, Python et machine learning. En français ou en anglais, en visioconférence et au Luxembourg.",
  },

  nav: { label: "Cours IA", cta: "Découvrir mes cours d’IA" },

  hero: {
    eyebrow: "COURS & ACCOMPAGNEMENTS",
    h1: "Cours particuliers et accompagnement en intelligence artificielle",
    subtitle:
      "Apprenez à utiliser l’IA ou à construire un projet concret avec un Data Scientist et AI Engineer. En français ou en anglais, en ligne et au Luxembourg.",
    ctaPrimary: "Discuter de mon objectif",
    ctaSecondary: "Voir les parcours",
    bullets: [
      "Séances individuelles adaptées à votre niveau",
      "Visioconférence depuis la France, le Luxembourg et ailleurs",
      "Présentiel au Luxembourg sur rendez-vous",
      "Français ou anglais, selon votre préférence",
    ],
  },

  parcoursTitle: "Trois parcours, un seul objectif : vous rendre autonome",
  parcoursSubtitle:
    "Choisissez le parcours qui correspond à votre niveau et à votre contexte. Nous ajustons ensuite le contenu à votre objectif précis.",

  parcours: [
    {
      id: "use-ai",
      badge: "A",
      title: "Utiliser l’IA au quotidien et au travail",
      audience:
        "Débutants, salariés, indépendants et personnes souhaitant mieux utiliser les outils d’IA.",
      bullets: [
        "Comprendre les possibilités et les limites de ChatGPT, Claude et des outils comparables.",
        "Formuler des demandes précises et obtenir des réponses utilisables.",
        "Améliorer, vérifier et recouper les réponses d’une IA.",
        "Travailler sur des textes, documents et informations réels.",
        "Construire une méthode adaptée à vos tâches habituelles.",
        "Comprendre les précautions pour vos données personnelles et professionnelles.",
      ],
      example:
        "Exemple de séance : nous travaillons sur une tâche réelle que vous choisissez, et nous en tirons une méthode réutilisable.",
      requirements: "Prérequis : aucun niveau en programmation requis.",
    },
    {
      id: "build-with-ai",
      badge: "B",
      title: "Créer un projet avec l’IA",
      audience:
        "Indépendants, entrepreneurs, étudiants et personnes ayant une idée de projet.",
      bullets: [
        "Définir un objectif et un périmètre réalistes.",
        "Choisir une approche et des outils adaptés.",
        "Créer progressivement un site, un prototype ou une automatisation.",
        "Comprendre, étape par étape, le résultat produit par l’IA.",
        "Tester, corriger et améliorer le prototype.",
        "Identifier les limites et les prochaines étapes.",
      ],
      example:
        "Exemple de séance : nous avançons ensemble sur un prototype simple, en comprenant chaque étape.",
      requirements:
        "À noter : il s’agit d’un accompagnement pédagogique. Aucune promesse d’application prête pour la production en une séance.",
    },
    {
      id: "python-ml",
      badge: "C",
      title: "Python, Data Science et Machine Learning",
      audience:
        "Étudiants, débutants en programmation et personnes souhaitant développer leurs compétences techniques.",
      bullets: [
        "Bases de Python : syntaxe, structures, bonnes pratiques.",
        "Préparation et exploration des données (pandas, visualisation).",
        "Introduction au machine learning : classification, régression, évaluation.",
        "Entraînement et évaluation des modèles sur des cas concrets.",
        "Compréhension et débogage de code existant.",
        "Accompagnement sur un projet personnel ou étudiant.",
      ],
      example:
        "Exemple de séance : nous construisons un modèle simple sur vos données, de la préparation à l’évaluation.",
      requirements:
        "Prérequis adaptés au sujet choisi. Pour un travail étudiant, l’objectif est d’expliquer et de vous rendre autonome.",
    },
  ],

  processTitle: "Comment se passe une séance",
  processSteps: [
    {
      step: "01",
      title: "Vous décrivez votre objectif",
      body: "Vous m’indiquez votre niveau, votre contexte et ce que vous voulez atteindre.",
    },
    {
      step: "02",
      title: "Nous convenons du cadre",
      body: "Nous définissons le contenu, le format, la langue et le tarif avant la réservation.",
    },
    {
      step: "03",
      title: "La séance combine théorie et pratique",
      body: "Explications claires et travail direct sur un cas concret, choisi avec vous.",
    },
    {
      step: "04",
      title: "Nous identifions les prochaines étapes",
      body: "À la fin, vous repartez avec une méthode, du code ou un prototype — et un plan clair pour continuer.",
    },
  ],

  credentialsTitle: "Pourquoi me faire confiance",
  credentialsIntro:
    "Mon expérience et mon parcours sont vérifiables sur ce site. Les expériences chez Thales Alenia Space et au Ministère des Armées font partie de mon parcours professionnel ; elles n’organisent ni ne recommandent ces cours particuliers.",
  credentialsItems: [
    {
      icon: "GraduationCap",
      title: "Formation",
      body: "Data Engineering & Intelligence Artificielle — ESILV.",
    },
    {
      icon: "Building2",
      title: "Expérience actuelle",
      body: "Data Scientist chez Thales Alenia Space, Luxembourg (depuis déc. 2025).",
    },
    {
      icon: "Shield",
      title: "Expérience passée",
      body: "Ministère des Armées / Marine Nationale — modèles ML et tableaux de bord.",
    },
    {
      icon: "GitBranch",
      title: "Projets publics",
      body: "Vision par ordinateur, Earth Observation, interprétabilité, prototypes full-stack.",
    },
  ],
  teachingNote:
    "Je donne également des cours de danse depuis un an et demi. L’enseignement fait partie de ma pratique régulière — cette expérience m’a appris à adapter mon niveau au vôtre et à rendre les choses simples.",
  recommendationNote:
    "Une recommandation professionnelle rédigée par François Robinet (chercheur en IA) figure déjà sur le portfolio. Il s’agit d’une recommandation professionnelle, pas d’un avis d’élève.",

  pricing: {
    title: "Tarifs",
    body: "Tarif confirmé avant réservation, selon le format et l’accompagnement choisi.",
  },

  form: {
    title: "Demander une séance",
    intro:
      "Dites-moi en quelques mots qui vous êtes et ce que vous aimeriez accomplir. Je reviens vers vous rapidement.",
    nameLabel: "Nom",
    emailLabel: "Email",
    pathLabel: "Parcours souhaité",
    pathPlaceholder: "Sélectionnez un parcours (optionnel)",
    levelLabel: "Votre niveau",
    levelPlaceholder: "Sélectionnez un niveau (optionnel)",
    levelOptions: [
      "Débutant complet",
      "Quelques bases",
      "Intermédiaire",
      "Avancé",
    ],
    objectiveLabel: "Votre objectif",
    objectivePlaceholder:
      "Décrivez votre objectif, votre contexte et un éventuel délai.",
    languageLabel: "Langue de la séance",
    languageOptions: [
      { value: "fr", label: "Français" },
      { value: "en", label: "English" },
    ],
    formatLabel: "Format",
    formatOptions: [
      { value: "online", label: "Visioconférence" },
      { value: "in-person", label: "Présentiel" },
    ],
    locationLabel: "Localisation (si présentiel)",
    locationPlaceholder: "Ville / pays",
    requiredNote: "* Champs obligatoires : nom, email, objectif.",
    submit: "Envoyer ma demande",
    submitting: "Envoi…",
    successTitle: "Demande envoyée",
    successBody:
      "Merci ! Je reviens vers vous rapidement avec un cadre et un tarif adaptés.",
    errorTitle: "Impossible d’envoyer la demande",
    errorBody:
      "Vous pouvez aussi m’écrire directement à louismelchiorgiraud@gmail.com.",
    pathOptions: [
      "A — Utiliser l’IA au quotidien",
      "B — Créer un projet avec l’IA",
      "C — Python, Data Science & Machine Learning",
      "Autre / je ne sais pas encore",
    ],
  },

  projectsTitle: "Projets pertinents",
  projectsIntro:
    "Quelques projets du portfolio en lien direct avec les parcours proposés.",
  projectsCta: "Voir tous les projets",
  relatedProjects: [
    {
      id: "drug-consumption",
      badge: "C",
      title: "Prédiction de consommation de drogues",
      body: "Pipeline de machine learning complet : préparation des données, comparaison de modèles, évaluation et interprétabilité.",
    },
    {
      id: "attrition",
      badge: "C",
      title: "Prédiction de l’attrition (Marine Nationale)",
      body: "Modèles SVM et CatBoost avec explications SHAP et restitution claire pour la prise de décision.",
    },
    {
      id: "xai-viz",
      badge: "A · C",
      title: "Visualisations d’IA explicable",
      body: "Outils pour rendre compréhensibles les prédictions d’un modèle — expliquer l’IA est au cœur de mes cours.",
    },
    {
      id: "language-app",
      badge: "B",
      title: "Application d’apprentissage des langues",
      body: "Projet full-stack personnel, du besoin au prototype fonctionnel : exactement la démarche du parcours B.",
    },
  ],

  ui: {
    navPaths: "Parcours",
    navProcess: "Déroulé",
    navTrust: "Parcours pro",
    navContact: "Contact",
    choosePath: "Choisir ce parcours",
    forWho: "Pour qui",
    stickyCta: "Discuter de mon objectif",
    langSwitchAria: "Switch to English",
    stepsLabel: "ÉTAPE",
    recommendationLink: "Lire la recommandation",
    contactDirect: "Ou écrivez-moi directement",
  },

  backToPortfolio: "Retour au portfolio",
};

/* ------------------------------------------------------------------ */
/*  English content (natural adaptation, not a word-for-word mirror)   */
/* ------------------------------------------------------------------ */
export const EN = {
  locale: "en",
  htmlLang: "en-GB",
  path: COACHING_PATHS.en,
  altPath: COACHING_PATHS.fr,
  altLabel: "Français",
  altLang: "fr-FR",

  seo: {
    title: "AI Coaching & Python Tutoring | Louis-Melchior Giraud",
    description:
      "Personalised AI, ChatGPT, Python and machine learning coaching with Louis-Melchior Giraud. Online and in Luxembourg, in English or French.",
    ogTitle: "Personalised AI coaching with Louis-Melchior Giraud",
    ogDescription:
      "One-to-one AI, ChatGPT, Python and machine learning tutoring. Online worldwide and in-person in Luxembourg, in English or French.",
  },

  nav: { label: "AI coaching", cta: "Explore AI coaching" },

  hero: {
    eyebrow: "COACHING & TUTORING",
    h1: "Personalised AI coaching and Python tutoring",
    subtitle:
      "Learn to use AI or build a practical project with a Data Scientist and AI Engineer. Available in English and French, online and in Luxembourg.",
    ctaPrimary: "Discuss my learning goals",
    ctaSecondary: "See learning paths",
    bullets: [
      "One-to-one sessions tailored to your level",
      "Video sessions from France, Luxembourg and anywhere else",
      "In-person in Luxembourg by appointment",
      "English or French, whichever works best for you",
    ],
  },

  parcoursTitle: "Three learning paths, one goal: make you autonomous",
  parcoursSubtitle:
    "Pick the path that matches your level and context. We then adjust the content to your specific objective.",

  parcours: [
    {
      id: "use-ai",
      badge: "A",
      title: "Using AI day-to-day and at work",
      audience:
        "Beginners, employees, freelancers and anyone who wants to get more value from AI tools.",
      bullets: [
        "Understand what ChatGPT, Claude and comparable tools can and cannot do.",
        "Formulate precise requests that give usable answers.",
        "Verify, cross-check and refine what an AI produces.",
        "Work on real texts, documents and information.",
        "Build a method that fits your actual tasks.",
        "Understand the precautions for personal and professional data.",
      ],
      example:
        "Example session: we work on a real task you pick, and extract a reusable method from it.",
      requirements: "Prerequisites: no programming background required.",
    },
    {
      id: "build-with-ai",
      badge: "B",
      title: "Building a project with AI",
      audience:
        "Freelancers, entrepreneurs, students and anyone with a project idea.",
      bullets: [
        "Define a realistic objective and scope.",
        "Choose an appropriate approach and toolset.",
        "Build, step by step, a site, prototype or automation.",
        "Understand what the AI produces at each step.",
        "Test, debug and improve the prototype.",
        "Identify the limits and the next steps.",
      ],
      example:
        "Example session: we move a simple prototype forward together, with a clear understanding of every step.",
      requirements:
        "Note: this is pedagogical coaching. No promise of a production-ready application in one session.",
    },
    {
      id: "python-ml",
      badge: "C",
      title: "Python, Data Science and Machine Learning",
      audience:
        "Students, programming beginners and anyone looking to level up their technical skills.",
      bullets: [
        "Python essentials: syntax, data structures, good practices.",
        "Data preparation and exploration (pandas, visualisation).",
        "Introduction to machine learning: classification, regression, evaluation.",
        "Train and evaluate models on concrete datasets.",
        "Read, understand and debug existing code.",
        "One-to-one support on a personal or academic project.",
      ],
      example:
        "Example session: we build a simple model on your data, end-to-end from preparation to evaluation.",
      requirements:
        "Prerequisites depend on the chosen topic. For academic work, the goal is to explain and make you autonomous — not to do the work for you.",
    },
  ],

  processTitle: "How a session works",
  processSteps: [
    {
      step: "01",
      title: "You describe your goal",
      body: "Share your current level, context and what you want to achieve.",
    },
    {
      step: "02",
      title: "We agree on the scope",
      body: "We define the content, format, language and price before booking.",
    },
    {
      step: "03",
      title: "The session blends theory and practice",
      body: "Clear explanations plus hands-on work on a concrete case we picked together.",
    },
    {
      step: "04",
      title: "We identify next steps",
      body: "You leave with a method, code or a prototype — and a clear plan to continue.",
    },
  ],

  credentialsTitle: "Why trust me",
  credentialsIntro:
    "My background is verifiable throughout the portfolio. Thales Alenia Space and the French Ministry of Armed Forces are part of my professional path; they do not organise or endorse this private coaching.",
  credentialsItems: [
    {
      icon: "GraduationCap",
      title: "Education",
      body: "Data Engineering & Artificial Intelligence — ESILV.",
    },
    {
      icon: "Building2",
      title: "Currently",
      body: "Data Scientist at Thales Alenia Space, Luxembourg (since Dec 2025).",
    },
    {
      icon: "Shield",
      title: "Previously",
      body: "French Ministry of Armed Forces / Navy — ML models and dashboards.",
    },
    {
      icon: "GitBranch",
      title: "Public projects",
      body: "Computer vision, Earth Observation, model interpretability, full-stack prototypes.",
    },
  ],
  teachingNote:
    "I also teach dance classes and have been doing so for a year and a half. Teaching is a regular part of my routine — it has taught me to adapt to the learner's level and keep things simple.",
  recommendationNote:
    "A professional recommendation written by François Robinet (AI researcher) is already featured on the portfolio. It is a professional recommendation, not a learner's review.",

  pricing: {
    title: "Pricing",
    body: "Pricing confirmed before booking, depending on the format and scope.",
  },

  form: {
    title: "Request a session",
    intro:
      "Tell me briefly who you are and what you'd like to achieve. I'll come back to you quickly.",
    nameLabel: "Name",
    emailLabel: "Email",
    pathLabel: "Preferred learning path",
    pathPlaceholder: "Select a path (optional)",
    levelLabel: "Your current level",
    levelPlaceholder: "Select a level (optional)",
    levelOptions: ["Complete beginner", "Some basics", "Intermediate", "Advanced"],
    objectiveLabel: "Your goal",
    objectivePlaceholder:
      "Describe your goal, your context and any timeline you have in mind.",
    languageLabel: "Session language",
    languageOptions: [
      { value: "en", label: "English" },
      { value: "fr", label: "Français" },
    ],
    formatLabel: "Format",
    formatOptions: [
      { value: "online", label: "Video call" },
      { value: "in-person", label: "In person" },
    ],
    locationLabel: "Location (if in person)",
    locationPlaceholder: "City / country",
    requiredNote: "* Required fields: name, email, goal.",
    submit: "Send my request",
    submitting: "Sending…",
    successTitle: "Request sent",
    successBody:
      "Thanks! I'll get back to you shortly with a tailored scope and price.",
    errorTitle: "Could not send the request",
    errorBody:
      "You can also reach me directly at louismelchiorgiraud@gmail.com.",
    pathOptions: [
      "A — Using AI day-to-day",
      "B — Building a project with AI",
      "C — Python, Data Science & Machine Learning",
      "Other / not sure yet",
    ],
  },

  projectsTitle: "Relevant projects",
  projectsIntro:
    "A few portfolio projects directly connected to the learning paths offered here.",
  projectsCta: "See all projects",
  relatedProjects: [
    {
      id: "drug-consumption",
      badge: "C",
      title: "Drug consumption prediction",
      body: "End-to-end machine learning pipeline: data preparation, model comparison, evaluation and interpretability.",
    },
    {
      id: "attrition",
      badge: "C",
      title: "Attrition prediction (French Navy)",
      body: "SVM and CatBoost models with SHAP explanations and clear, decision-oriented reporting.",
    },
    {
      id: "xai-viz",
      badge: "A · C",
      title: "Explainable AI visualisations",
      body: "Tools that make model predictions understandable — explaining AI is at the heart of my sessions.",
    },
    {
      id: "language-app",
      badge: "B",
      title: "Language-learning app",
      body: "Personal full-stack project, from need to working prototype: exactly the approach of path B.",
    },
  ],

  ui: {
    navPaths: "Paths",
    navProcess: "How it works",
    navTrust: "Background",
    navContact: "Contact",
    choosePath: "Choose this path",
    forWho: "Who it's for",
    stickyCta: "Discuss my goals",
    langSwitchAria: "Passer en français",
    stepsLabel: "STEP",
    recommendationLink: "Read the recommendation",
    contactDirect: "Or email me directly",
  },

  backToPortfolio: "Back to portfolio",
};

export const COACHING_CONTENT = { fr: FR, en: EN };

export function buildCoachingSeo(t) {
  const url = (p) => `${SITE_URL}${p}`;
  return {
    title: t.seo.title,
    description: t.seo.description,
    ogTitle: t.seo.ogTitle,
    ogDescription: t.seo.ogDescription,
    lang: t.htmlLang,
    ogLocale: t.locale === "fr" ? "fr_FR" : "en_GB",
    canonical: url(t.path),
    image: url("/og-preview.jpg"),
    alternates: [
      { hrefLang: "fr", href: url(COACHING_PATHS.fr) },
      { hrefLang: "en", href: url(COACHING_PATHS.en) },
      { hrefLang: "x-default", href: url(COACHING_PATHS.en) },
    ],
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: t.hero.h1,
      description: t.seo.description,
      url: url(t.path),
      serviceType: t.locale === "fr" ? "Cours particuliers d’IA et de Python" : "AI coaching and Python tutoring",
      availableLanguage: ["fr", "en"],
      areaServed: [
        { "@type": "Country", name: "Luxembourg" },
        { "@type": "Country", name: "France" },
      ],
      provider: {
        "@type": "Person",
        name: "Louis-Melchior Giraud",
        alternateName: "Bluebloodfr",
        jobTitle: "Data Scientist & AI Engineer",
        url: SITE_URL,
        sameAs: [
          "https://github.com/Bluebloodfr",
          "https://www.linkedin.com/in/louis-melchior-giraud",
        ],
      },
    },
  };
}
